import { useContext } from 'react';
import { Link } from 'react-router-dom';
import { SidebarContext } from 'src/contexts/SidebarContext';
import SidebarMenu from './SidebarMenu';
import { MobileDrawer } from 'src/components/ui';
import logoImage from '../../../Images/Logo/symbian-logo.svg';
import smallLogoImage from '../../../Images/Logo/Symbian-logo-small.png';
import { useThemeMode } from '../../../theme/ThemeProvider';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const EXPANDED_W = 210;
const COLLAPSED_W = 72;

const EASE = 'cubic-bezier(0.22,1,0.36,1)';
const DURATION = '280ms';
const TRANSITION = `width ${DURATION} ${EASE}`;

function Sidebar() {
  const { sidebarToggle, toggleSidebar, isCollapsed, toggleCollapse } = useContext(SidebarContext);
  const { isDark } = useThemeMode();

  const sidebarW = isCollapsed ? COLLAPSED_W : EXPANDED_W;

  return (
    <>
      <aside
        className={[
          'hidden lg:flex flex-col fixed left-0 top-0 bottom-0 overflow-y-auto overflow-x-hidden z-[1201]',
          '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          isDark
            ? 'bg-dark-chrome-sidebar/95 shadow-dark-sidebar'
            : 'bg-white/[0.98] shadow-[4px_0_32px_-4px_rgba(0,0,0,0.09),1px_0_0_rgba(0,0,0,0.04)]',
        ].join(' ')}
        style={{
          width: sidebarW,
          transition: TRANSITION,
          willChange: 'width',
          backdropFilter: 'blur(32px) saturate(1.6)',
          WebkitBackdropFilter: 'blur(32px) saturate(1.6)',
        }}
      >
        <div
          className={[
            'absolute right-0 top-0 bottom-0 w-px pointer-events-none z-10',
            isDark
              ? 'bg-gradient-to-b from-dark-border-brand via-dark-border-subtle to-dark-border-glow'
              : 'bg-gradient-to-b from-slate-200/60 via-slate-200 to-slate-200/40',
          ].join(' ')}
        />

        {isDark && (
          <div className="absolute inset-0 pointer-events-none z-0 bg-[radial-gradient(ellipse_80%_50%_at_100%_20%,var(--tw-gradient-from),transparent_70%)] from-dark-glow-brand-subtle" />
        )}

        <div
          className={[
            'relative flex items-center h-[64px] flex-shrink-0 z-[1]',
            'transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
            isCollapsed ? 'justify-center px-0' : 'px-5',
          ].join(' ')}
        >
          <Link to="/" className="relative flex items-center justify-center overflow-hidden w-full h-[44px] group">
            <img
              src={logoImage}
              alt="Symbian Health"
              className={[
                'absolute left-0 object-contain h-[32px] lg:h-[34px] xl:h-[36px] w-auto max-w-[140px] lg:max-w-[148px] xl:max-w-[156px]',
                'transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] origin-left',
                isCollapsed
                  ? 'opacity-0 invisible pointer-events-none -translate-x-2'
                  : 'opacity-100 visible pointer-events-auto translate-x-0 group-hover:opacity-80 group-hover:scale-[1.02]',
                isDark ? 'brightness-0 invert' : '',
              ].join(' ')}
            />
            {/* Collapsed logo — centered via transform (immune to container width) */}
            <img
              src={smallLogoImage}
              alt="Symbian"
              className={[
                'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
                'object-contain w-[48px] h-[48px]',
                'transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
                isCollapsed
                  ? 'opacity-100 visible pointer-events-auto scale-100 group-hover:opacity-80 group-hover:scale-[1.1]'
                  : 'opacity-0 invisible pointer-events-none scale-75',
                isDark ? 'brightness-0 invert' : '',
              ].join(' ')}
            />
          </Link>

          <div
            className={[
              'absolute bottom-0 h-px',
              isCollapsed ? 'left-2 right-2' : 'left-0 right-0',
              isDark
                ? 'bg-gradient-to-r from-transparent via-dark-border-brand/40 to-transparent'
                : 'bg-gradient-to-r from-transparent via-brand-500/[0.18] to-transparent',
            ].join(' ')}
          />
        </div>

        <div className="relative z-[1] flex-1 flex flex-col min-h-0 overflow-hidden">
          <SidebarMenu />
        </div>
      </aside>

      <div
        className="hidden lg:block fixed z-[1400]"
        style={{
          left: sidebarW,
          top: 52,
          transition: `left ${DURATION} ${EASE}`,
          willChange: 'left',
        }}
        aria-hidden="true"
      >
        <div style={{ transform: 'translateX(-50%)' }}>
          <button
            onClick={toggleCollapse}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!isCollapsed}
            className={[
              'w-6 h-6 rounded-full',
              'flex items-center justify-center',
              'cursor-pointer select-none',
              'transition-all duration-150',
              'hover:scale-110 active:scale-90',
              'border',
              isDark
                ? [
                  'bg-[#0e1830]',
                  'border-white/[0.18]',
                  'text-slate-400',
                  'hover:text-white',
                  'hover:border-brand-400/60',
                  'shadow-[0_2px_12px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.04)]',
                  'hover:shadow-[0_0_0_3px_rgba(43,141,224,0.15),0_2px_12px_rgba(0,0,0,0.6)]',
                ].join(' ')
                : [
                  'bg-white',
                  'border-slate-200',
                  'text-slate-400',
                  'hover:text-brand-600',
                  'hover:border-brand-300',
                  'shadow-[0_2px_8px_-1px_rgba(0,0,0,0.10),0_1px_4px_-1px_rgba(0,0,0,0.06)]',
                  'hover:shadow-[0_0_0_3px_rgba(3,93,184,0.10),0_2px_8px_-1px_rgba(0,0,0,0.10)]',
                ].join(' '),
            ].join(' ')}
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={isCollapsed ? 'right' : 'left'}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
                transition={{ duration: 0.14, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-center justify-center"
              >
                {isCollapsed
                  ? <ChevronRight size={11} strokeWidth={2.8} />
                  : <ChevronLeft size={11} strokeWidth={2.8} />
                }
              </motion.span>
            </AnimatePresence>
          </button>
        </div>
      </div>

      <MobileDrawer
        open={sidebarToggle}
        onClose={toggleSidebar}
        title="Navigation"
      >
        <SidebarMenu />
      </MobileDrawer>
    </>
  );
}

export default Sidebar;
